import React, { useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import classNames from "classnames";

import Button from "@/shared/components/Button/Button";
import IconView from "@/shared/components/IconView/IconView";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import EgwWebFont from "@/shared/assets/EgwWebFont";
import Modal from "@/shared/components/modal/Modal";

import styles from "./date-switcher.module.scss";
import { EL_ID_PORTAL_MAIN_AFTER } from "@/shared/components/Portal/PortalMainAfter/constants";
import Portal from "@/shared/components/Portal/Portal";

const formatDate = (date) => dayjs(date).format("DD.MM");

const DateSwitcher = ({ SubscriptionsDatePicker, date, onDateChange, className }) => {
  const { t } = useSharedTranslation();
  const [localModalContent, setLocalModalContent] = useState(null);

  const openCalendar = () => {
    setLocalModalContent(<SubscriptionsDatePicker date={date} onDateChange={handleModalDateChange} />)
  }

  const closeCalendar = () => {
    setLocalModalContent(null)
  }

  const [yesterday, tomorrow, isActualTomorrow] = useMemo(() => {
    const tomorrow = dayjs(date).add(1, "day");
    const yesterday = dayjs(date).subtract(1, "day");
    const isActualTomorrow = dayjs(tomorrow).isAfter(new Date());
    return [yesterday, tomorrow, isActualTomorrow];
  }, [date]);

  const handleDateChange = useCallback(
    (next) => {
      if (next && isActualTomorrow) {
        return;
      }

      let changedDate;

      if (next) {
        changedDate = tomorrow;
      } else {
        changedDate = yesterday;
      }

      onDateChange(changedDate.toDate());
    },
    [yesterday, onDateChange, tomorrow, isActualTomorrow],
  );

  const handleModalDateChange = (newDate) => {
    onDateChange(newDate);
    closeCalendar();
  };

  return (
    <div className={classNames(styles["date-switcher"], className)}>
      <Button
        title={t("calendar")}
        className={styles["date-switcher-icon-calendar"]}
        onClick={openCalendar}>
        <IconView icon={EgwWebFont.calendar} />
      </Button>
      <div className={styles["date-switcher-controls-container"]}>
        <div className={styles["date-switcher-date-container"]} onClick={() => handleDateChange()}>
          <IconView
            icon={EgwWebFont["chevron-left"]}
            className={styles["date-switcher-date-icon"]}
          />
          <span className={styles["date-switcher-date-text"]}>{formatDate(yesterday)}</span>
        </div>
        <span className={styles["date-switcher-date-current"]}>{formatDate(date)}</span>
        <div
          className={classNames(styles["date-switcher-date-container"], {
            [styles["is-disabled"]]: isActualTomorrow,
          })}
          onClick={() => handleDateChange(true)}>
          <span className={styles["date-switcher-date-text"]}>{formatDate(tomorrow)}</span>
          <IconView
            icon={EgwWebFont["chevron-right"]}
            className={styles["date-switcher-date-icon"]}
          />
        </div>
      </div>
      <Portal id={EL_ID_PORTAL_MAIN_AFTER}>
        {localModalContent && <Modal onClose={closeCalendar} props={{ component: localModalContent }} />}
      </Portal>
    </div>
  );
};

DateSwitcher.propTypes = {
  SubscriptionsDatePicker: PropTypes.any,
  date: PropTypes.object,
  onDateChange: PropTypes.func,
  className: PropTypes.string,
};

export default DateSwitcher;
